<script setup lang="ts">
import Signpost from '#assets/svg/signpost.svg';
import { useFilterDefinitionValue } from '#root/composables/filterDefinition/useFilterDefinitionValue.ts';
import { useLanguage } from '#root/composables/useLanguage.ts';
import { useUserStore } from '#stores/user.store';
import {
  C4ButtonBase,
  C4CardCheckBox,
  C4CardCheckBoxes,
  C4CheckBoxGrid,
  C4SpinnerLoader,
  CheckBoxTree,
  InputSearch,
  type InputSearchModelType
} from '@c4-links/vue3-links-components';
import {
  FilterDefinitionFormType,
  FilterDefinitionValueSearchType,
  type FilterDefinitionValue
} from '@carrefour-gcs/bff-sdk';
import { CAccordion, CIcon } from '@carrefour/design-system-components-vue3';
import { refDebounced } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useCheckboxCardSelection } from './composables/useCheckboxCardSelection';
import { useCheckboxGridSelection } from './composables/useCheckboxGridSelection';
import { useCheckboxGroupSelection } from './composables/useCheckboxGroupSelection';
import { useCheckboxTreeSelection } from './composables/useCheckboxTreeSelection';
import { useSearchMandatoryTreeOptions } from './composables/useSearchMandatoryTreeOptions';
import { type AccordionFieldProps } from './types/accordionFieldProps.type';
import { getSearchInputOptions } from './utils/searchInput.utils';
import { getSearchTagSeparators } from './utils/searchTagSeparator.utils';

const { t } = useLanguage();
const userStore = useUserStore();
const searchInputModel = ref<InputSearchModelType>({ searchValue: undefined, searchType: undefined });
const debouncedSearch = refDebounced(searchInputModel, 800);

const emit = defineEmits<{
  (e: 'update:fieldValue', payload: unknown): void;
}>();
const { field, shouldShowIcon, disabled } = defineProps<AccordionFieldProps>();

const nbElementsSelected = computed(() => {
  if (field.element === FilterDefinitionFormType.CHECKBOX_TREE) {
    return nbSelectedCheckboxTreeItems.value;
  }

  if (field.element === FilterDefinitionFormType.CHECKBOX_CARD) {
    return nbSelectedCheckboxItems.value;
  }

  if (field.element === FilterDefinitionFormType.CHECKBOX_GROUP) {
    return nbSelectedCheckboxGroupItems.value;
  }

  if (field.element === FilterDefinitionFormType.CHECKBOX_GRID) {
    return nbSelectedCheckboxGridItems.value;
  }

  return 0;
});

/**
 * A search-mandatory filter only displays its selected values until the user has typed a search.
 */
const isWaitingForMandatorySearch = computed(() => {
  if (!field.isSearchMandatory) {
    return false;
  }

  const searchValue = debouncedSearch.value?.searchValue;
  const searchValues = Array.isArray(searchValue) ? searchValue : [searchValue];

  return !searchValues.some((value) => !!value?.trim());
});

const selectDeselectClass = computed(() => ({
  'text-gray-400 cursor-not-allowed':
    (checkboxTreeDataRef.value.length === 0 &&
      checkboxCardValues.value.length === 0 &&
      checkboxGroupValues.value.length === 0 &&
      checkboxGridModelValue.value.length === 0 &&
      !isFetching.value) ||
    isFetching.value,
  'cursor-pointer':
    !isFetching.value &&
    (checkboxTreeDataRef.value.length > 0 ||
      checkboxCardValues.value.length > 0 ||
      checkboxGroupValues.value.length > 0 ||
      checkboxGridModelValue.value.length > 0)
}));

const {
  data: filterDefinitionData,
  showMore,
  isFetching,
  isFetchingMore,
  resetPagination,
  fetchValuesByKeys
} = useFilterDefinitionValue(field, debouncedSearch);

/**
 * "Show more" only works on searched values (the query is disabled without search),
 * so it is displayed while the API has more values than the ones already loaded.
 */
const shouldShowMore = computed(() => {
  const searchResult = filterDefinitionData.value;

  if (!field.isPaginated || (isFetching.value && !isFetchingMore.value) || !searchResult?.data?.length) {
    return false;
  }

  // The total counts the deepest level of a tree (one row per leaf), not its roots.
  const nbLoaded =
    field.element === FilterDefinitionFormType.CHECKBOX_TREE
      ? countTreeLeaves(searchResult.data)
      : searchResult.data.length;

  return (searchResult.total ?? 0) > nbLoaded;
});

function countTreeLeaves(nodes: FilterDefinitionValue[]): number {
  return nodes.reduce((count, node) => count + (node.children?.length ? countTreeLeaves(node.children) : 1), 0);
}

// ——— B ——— CHECKBOX TREE ——————————————————————————————————————————————————————————————————————————————————————————————————

const isSearchMandatoryTree = computed(
  () => field.isSearchMandatory && field.element === FilterDefinitionFormType.CHECKBOX_TREE
);

const { options: searchMandatoryTreeOptions, onSelectionChange: onSearchMandatoryTreeSelectionChange } =
  useSearchMandatoryTreeOptions({
    isEnabled: isSearchMandatoryTree,
    initialValue: computed(() => field.initialValue),
    searchInput: searchInputModel,
    searchData: computed(() => filterDefinitionData.value?.data),
    fetchValuesByKeys
  });

const {
  selectAll: selectAllCheckboxTree,
  unselectAll: unselectAllCheckboxTree,
  onTreeUpdate,
  checkboxTreeDataRef,
  nbSelectedCheckboxTreeItems,
  reset: resetTree
} = useCheckboxTreeSelection({
  isEnabled: computed(() => field.element === FilterDefinitionFormType.CHECKBOX_TREE),
  searchInput: searchInputModel,
  initialValue: computed(() => field.initialValue),
  searchData: computed(() => filterDefinitionData.value?.data),
  fieldOptions: computed(() =>
    isSearchMandatoryTree.value
      ? searchMandatoryTreeOptions.value
      : (field.options as FilterDefinitionValue[] | undefined)
  ),
  onSelectionChange: (selections) => {
    if (field.element === FilterDefinitionFormType.CHECKBOX_TREE) {
      onSearchMandatoryTreeSelectionChange(selections);
      emit('update:fieldValue', selections);
    }
  }
});

const resetCheckboxTreeSelections = () => {
  resetTree();
  resetPagination();
  emit('update:fieldValue', []);
};

// ——— E ——— CHECKBOX TREE ——————————————————————————————————————————————————————————————————————————————————————————————————

// ——— B ——— CHECKBOX CARD ——————————————————————————————————————————————————————————————————————————————————————————————————

const {
  checkboxCardValues,
  nbSelectedCheckboxItems,
  modelValue: cardCheckBoxModelValue,
  initialize: initializeCheckboxCard,
  selectAll: selectAllCheckboxCard,
  deselectAll: deselectAllCheckboxCard
} = useCheckboxCardSelection({
  field,
  searchValue: debouncedSearch,
  initialValue: computed(() => field.initialValue),
  searchData: computed(() => filterDefinitionData.value?.data),
  onSelectionChange: (selections) => {
    if (field.element === FilterDefinitionFormType.CHECKBOX_CARD) {
      emit('update:fieldValue', selections);
    }
  }
});

// ——— E ——— CHECKBOX CARD ——————————————————————————————————————————————————————————————————————————————————————————————————

// ——— B ——— CHECKBOX GROUP —————————————————————————————————————————————————————————————————————————————————————————————————

const {
  checkboxGroupValues,
  nbSelectedCheckboxGroupItems,
  modelValue: checkboxGroupModelValue,
  initialize: initializeCheckboxGroup,
  selectAll: selectAllCheckboxGroup,
  deselectAll: deselectAllCheckboxGroup
} = useCheckboxGroupSelection({
  field,
  searchValue: debouncedSearch,
  initialValue: computed(() => field.initialValue),
  searchData: computed(() => filterDefinitionData.value?.data),
  onSelectionChange: (selections) => {
    if (field.element === FilterDefinitionFormType.CHECKBOX_GROUP) {
      emit('update:fieldValue', selections);
    }
  }
});

// ——— E ——— CHECKBOX GROUP —————————————————————————————————————————————————————————————————————————————————————————————————

// ——— B ——— CHECKBOX GRID —————————————————————————————————————————————————————————————————————————————————————————————————

const {
  nbSelectedCheckboxGridItems,
  modelValue: checkboxGridModelValue,
  initialize: initializeCheckboxGrid,
  selectAll: selectAllCheckboxGrid,
  deselectAll: deselectAllCheckboxGrid
} = useCheckboxGridSelection({
  field,
  searchValue: debouncedSearch,
  initialValue: computed(() => field.initialValue),
  searchData: computed(() => filterDefinitionData.value?.data),
  fetchValuesByKeys,
  isAnonymized: computed(() => userStore.isAnonymized),
  onSelectionChange: (selections) => {
    if (field.element === FilterDefinitionFormType.CHECKBOX_GRID) {
      emit('update:fieldValue', selections);
    }
  }
});

// ——— E ——— CHECKBOX GRID —————————————————————————————————————————————————————————————————————————————————————————————————

/**
 * Selects every displayed value, whatever the kind of checkbox field.
 */
const selectAllCheckboxes = () => {
  selectAllCheckboxTree();
  selectAllCheckboxCard();
  selectAllCheckboxGroup();
  selectAllCheckboxGrid();
};

const selectOrDeselectAllCheckbox = ({ selectAll }: { selectAll: boolean }) => {
  if (isFetching.value) {
    return;
  }

  if (selectAll) {
    selectAllCheckboxes();
  } else {
    unselectAllCheckboxTree();
    deselectAllCheckboxCard();
    deselectAllCheckboxGroup();
    deselectAllCheckboxGrid();
  }
};

// ——— B ——— MULTISELECT AUTO SELECTION ———————————————————————————————————————————————————————————————————————————————————————

/**
 * Set when a new multiselect search is submitted, consumed once its results are displayed,
 * so loading more results ("show more") does not override the user's selection.
 */
const isMultiselectAutoSelectPending = ref(false);

watch(debouncedSearch, (search) => {
  const searchValue = search?.searchValue;
  const hasSearchValue = Array.isArray(searchValue) ? searchValue.length > 0 : !!searchValue?.trim();

  isMultiselectAutoSelectPending.value =
    search?.searchType === FilterDefinitionValueSearchType.MULTISELECT && hasSearchValue;
});

// Runs after the render so the selection composables have already displayed the search results
watch(
  () => filterDefinitionData.value?.data,
  (results) => {
    if (!isMultiselectAutoSelectPending.value || !results?.length) {
      return;
    }

    isMultiselectAutoSelectPending.value = false;
    selectAllCheckboxes();
  },
  { flush: 'post' }
);

// ——— E ——— MULTISELECT AUTO SELECTION ———————————————————————————————————————————————————————————————————————————————————————

defineExpose({ resetCheckboxTreeSelections });

onMounted(() => {
  if (field.element === FilterDefinitionFormType.CHECKBOX_CARD) {
    initializeCheckboxCard();
  } else if (field.element === FilterDefinitionFormType.CHECKBOX_GROUP) {
    initializeCheckboxGroup();
  } else if (field.element === FilterDefinitionFormType.CHECKBOX_GRID) {
    initializeCheckboxGrid();
  }
});
</script>

<template>
  <CAccordion title="" :disabled="disabled" :expanded="false" :badge="nbElementsSelected.toString()">
    <template #title>
      <div class="pl-4 w-full flex items-center">
        <Signpost v-if="field.icon === 'signpost'" class="[&_path]:fill-black mr-1"></Signpost>

        <CIcon v-if="shouldShowIcon" class="mr-2 text-2xl" :name="field.icon"></CIcon>
        <label class="flex items-center text-sm" :for="field.fieldName">
          {{ t(field.fieldLabel) }}
        </label>
      </div>
    </template>

    <template #default>
      <template v-if="field.isSearchable">
        <!-- The focused search (dropdown open) stacks above the search of the next accordions -->
        <div class="search-input relative z-10 focus-within:z-20 p-4 pb-1 w-full">
          <InputSearch
            v-model="searchInputModel"
            size="medium"
            :label="t('filters.form.search.label')"
            :name="`search-filter-${field.fieldName}`"
            :search-options="getSearchInputOptions(t)"
            :tag-separator="getSearchTagSeparators(field.fieldName)"
          />
        </div>
      </template>
      <div class="py-2">
        <div class="flex items-center gap-x-2 px-4 text-xs text-interactive-active-main-primary">
          <a class="ml-auto" :class="selectDeselectClass" @click="selectOrDeselectAllCheckbox({ selectAll: true })">
            {{ t('filters.form.select-all.label') }}
          </a>

          <span :class="selectDeselectClass">●</span>

          <a :class="selectDeselectClass" @click="selectOrDeselectAllCheckbox({ selectAll: false })">
            {{ t('filters.form.unselect-all.label') }}
          </a>
        </div>

        <CheckBoxTree
          v-if="
            checkboxTreeDataRef.length !== 0 &&
            (!isFetching || isFetchingMore) &&
            field.element === FilterDefinitionFormType.CHECKBOX_TREE
          "
          v-model="checkboxTreeDataRef"
          :last-divider="false"
          @update-data="onTreeUpdate"
        />

        <template
          v-if="
            field.element === FilterDefinitionFormType.CHECKBOX_CARD && !isFetching && checkboxCardValues.length > 0
          "
        >
          <div class="p-4 grid grid-cols-2 gap-4">
            <C4CardCheckBox
              v-for="cardCheckbox in checkboxCardValues"
              :key="cardCheckbox.id"
              v-bind="cardCheckbox"
              v-model="cardCheckBoxModelValue[cardCheckbox.value]"
            />
          </div>
        </template>

        <template
          v-if="
            field.element === FilterDefinitionFormType.CHECKBOX_GROUP && !isFetching && checkboxGroupValues.length > 0
          "
        >
          <div class="p-4 flex flex-col gap-4">
            <C4CardCheckBoxes
              v-for="group in checkboxGroupValues"
              :key="group.id"
              v-bind="group"
              v-model="checkboxGroupModelValue[group.id as string]"
            />
          </div>
        </template>

        <template
          v-if="
            field.element === FilterDefinitionFormType.CHECKBOX_GRID &&
            (!isFetching || isFetchingMore) &&
            checkboxGridModelValue.length > 0
          "
        >
          <div class="p-4 flex flex-col gap-4">
            <C4CheckBoxGrid v-model="checkboxGridModelValue" :nb-grid-columns="2" />
          </div>
        </template>

        <div v-else-if="isFetching" class="text-center py-4">
          <C4SpinnerLoader />
        </div>

        <div
          v-else-if="
            checkboxTreeDataRef.length === 0 &&
            checkboxCardValues.length === 0 &&
            checkboxGroupValues.length === 0 &&
            checkboxGridModelValue.length === 0 &&
            !isFetching
          "
          class="text-center py-4 text-gray-600"
        >
          {{ t(isWaitingForMandatorySearch ? 'filters.form.search-mandatory.label' : 'commonLabel.noResult') }}
        </div>
      </div>

      <div v-if="isFetchingMore" class="text-center py-2">
        <C4SpinnerLoader />
      </div>
      <C4ButtonBase
        v-else-if="shouldShowMore"
        class="w-full m-1"
        tone="main"
        variation="tertiary"
        :label="t('filters.form.show-more.label')"
        @click="showMore"
      />
    </template>
  </CAccordion>
</template>

<style src="./accordionField.css" scoped />
